import React, { createContext, useReducer, useContext, useMemo } from 'react';

type ActionType = 'SET_SELECTED_EMPLOYEE_IDS' | 'RESET_SELECTED' | 'SET_TOTAL_EMPLOYEES';

interface State {
  selectedEmployeeIds: number[];
  totalEmployees: number;
}

const STATE: State = {
  selectedEmployeeIds: [],
  totalEmployees: 0,
};

type Reducer = (state: State, action: { type: ActionType; payload?: any }) => typeof STATE;

interface BulkActionContextType extends State {
  resetSelection: () => void;
  setTotalEmployees: (total: number) => void;
}

const BulkActionContext = createContext<BulkActionContextType>(null);

export const BulkActionContextProvider = ({ children }) => {
  // eslint-disable-next-line no-shadow
  const [state, dispatch] = useReducer<Reducer>((state, action) => {
    switch (action.type) {
      case 'SET_SELECTED_EMPLOYEE_IDS': {
        return {
          ...state,
          selectedEmployeeIds: action.payload,
        };
      }

      case 'RESET_SELECTED': {
        return {
          ...state,
          selectedEmployeeIds: [],
        };
      }

      case 'SET_TOTAL_EMPLOYEES': {
        return {
          ...state,
          totalEmployees: action.payload,
        };
      }
      default: {
        return { ...state };
      }
    }
  }, STATE);

  const setSelectedEmployeeIds = (ids: string[]) => {
    dispatch({ type: 'SET_SELECTED_EMPLOYEE_IDS', payload: ids });
  };

  const resetSelection = () => {
    dispatch({ type: 'RESET_SELECTED' });
  };

  const setTotalEmployees = (total: number) => {
    dispatch({ type: 'SET_TOTAL_EMPLOYEES', payload: total });
  };

  const memoizedState = useMemo(
    () => ({
      ...state,
      setSelectedEmployeeIds,
      resetSelection,
      setTotalEmployees,
    }),
    [state]
  );

  return <BulkActionContext.Provider value={memoizedState}>{children}</BulkActionContext.Provider>;
};

export const useBulkActionContext = () => useContext(BulkActionContext);
